// components/public/AffordabilityModal.tsx
/**
 * @description Affordability calculator modal
 * Helps users determine which units fit their budget
 */
import { useState } from 'react';
import { X, Check, AlertCircle, DollarSign } from 'lucide-react';
import { formatPrice } from '../../lib/utils';
import type { Unit } from '../../types/database';

interface AffordabilityModalProps {
  isOpen: boolean;
  onClose: () => void;
  units: Unit[];
  onFilterByBudget: (maxRent: number) => void;
}

export function AffordabilityModal({ isOpen, onClose, units, onFilterByBudget }: AffordabilityModalProps) {
  const [income, setIncome] = useState('');
  const [showResults, setShowResults] = useState(false);

  if (!isOpen) return null;

  const monthlyIncome = parseInt(income.replace(/[^0-9]/g, ''), 10) || 0;
  const recommendedMax = Math.floor(monthlyIncome * 0.3);

  const affordableUnits = units.filter(u => u.price <= recommendedMax);
  const unaffordableUnits = units.filter(u => u.price > recommendedMax);

  const handleCalculate = () => {
    if (monthlyIncome > 0) {
      setShowResults(true);
    }
  };

  const handleViewAffordable = () => {
    onFilterByBudget(recommendedMax);
    onClose();
  };

  const handleReset = () => {
    setIncome('');
    setShowResults(false);
  };

  const formatInputValue = (value: string) => {
    const digits = value.replace(/[^0-9]/g, '');
    if (!digits) return '';
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: 'CAD',
      maximumFractionDigits: 0
    }).format(parseInt(digits, 10));
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 z-[100] animate-fade-in"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="fixed inset-x-4 top-1/2 -translate-y-1/2 bg-white rounded-2xl z-[101] max-w-[400px] mx-auto animate-scale-in overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <DollarSign size={20} className="text-primary" />
            <h2 className="font-semibold text-gray-900">Can I Afford This?</h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X size={20} className="text-gray-500" />
          </button>
        </div>

        <div className="p-4">
          {!showResults ? (
            <>
              {/* Input Section */}
              <p className="text-sm text-gray-600 mb-4">
                Enter your combined monthly income before taxes. We recommend spending no more than 30% of your income on rent.
              </p>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Monthly Income (before taxes)
                </label>
                <input
                  type="text"
                  value={income}
                  onChange={(e) => setIncome(formatInputValue(e.target.value))}
                  placeholder="$5,000"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg text-lg font-medium focus:ring-2 focus:ring-primary focus:border-primary"
                />
              </div>

              <button
                onClick={handleCalculate}
                disabled={monthlyIncome === 0}
                className="w-full py-3 bg-primary hover:bg-primary-hover disabled:bg-gray-200 disabled:text-gray-400 text-white rounded-lg font-medium transition-colors"
              >
                Calculate
              </button>
            </>
          ) : (
            <>
              {/* Results Section */}
              <div className="text-center mb-6">
                <p className="text-sm text-gray-500 mb-1">Based on your income</p>
                <p className="text-3xl font-bold text-primary">
                  {formatPrice(recommendedMax)}
                </p>
                <p className="text-sm text-gray-600">recommended max rent/month</p>
              </div>

              {/* Units List */}
              <div className="space-y-3 max-h-[200px] overflow-y-auto mb-4">
                {affordableUnits.map(unit => (
                  <div key={unit.id} className="flex items-center gap-3 p-3 bg-green-50 rounded-lg">
                    <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <Check size={16} className="text-green-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-900 text-sm truncate">
                        {unit.unit_type?.name} - Unit {unit.unit_number}
                      </p>
                      <p className="text-xs text-gray-500">Floor {unit.floor}</p>
                    </div>
                    <p className="font-semibold text-green-600 text-sm">
                      {formatPrice(unit.price)}
                    </p>
                  </div>
                ))}

                {unaffordableUnits.map(unit => (
                  <div key={unit.id} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg opacity-60">
                    <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center flex-shrink-0">
                      <AlertCircle size={16} className="text-gray-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-500 text-sm truncate">
                        {unit.unit_type?.name} - Unit {unit.unit_number}
                      </p>
                      <p className="text-xs text-gray-400">Floor {unit.floor}</p>
                    </div>
                    <p className="font-semibold text-gray-400 text-sm">
                      {formatPrice(unit.price)}
                    </p>
                  </div>
                ))}
              </div>

              {/* Summary */}
              <p className="text-sm text-gray-600 text-center mb-4">
                {affordableUnits.length} of {units.length} units fit your budget
              </p>

              {/* Actions */}
              <div className="flex gap-3">
                <button
                  onClick={handleReset}
                  className="flex-1 py-3 border border-gray-300 text-gray-700 rounded-lg font-medium text-sm hover:bg-gray-50 transition-colors"
                >
                  Try Again
                </button>
                {affordableUnits.length > 0 && (
                  <button
                    onClick={handleViewAffordable}
                    className="flex-1 py-3 bg-primary hover:bg-primary-hover text-white rounded-lg font-medium text-sm transition-colors"
                  >
                    View {affordableUnits.length} Units
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
}
